import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  InputLabel,
  FormControl,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

const DeviceImageUploader = ({ onSelectImage, initialValue }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const fileInputRef = useRef(null);

  const [existingImages, setExistingImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(initialValue || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(initialValue || "");
  const [isLocalFile, setIsLocalFile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load existing images on component mount
  useEffect(() => {
    let isMounted = true;

    const fetchImages = async () => {
      try {
        setIsLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/list`
        );
        const data = await res.json();

        if (!isMounted) return;

        setExistingImages(data);

        // Only set default if no initialValue was provided
        if (!initialValue && data.length > 0) {
          setSelectedImage(data[0].url);
          setPreview(data[0].url);
          onSelectImage(data[0].url);
        } else if (initialValue) {
          // If initialValue exists, make sure it's selected
          setSelectedImage(initialValue);
          setPreview(initialValue);
        }
      } catch (err) {
        console.error("Error fetching images:", err);
        if (isMounted) {
          toast.error("Failed to load existing images.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchImages();

    return () => {
      isMounted = false;
      // Clean up any local file URL
      if (preview && isLocalFile) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [initialValue, onSelectImage, isLocalFile, preview]);

  // Effect to handle initialValue changes
  useEffect(() => {
    if (initialValue && initialValue !== selectedImage) {
      setSelectedImage(initialValue);
      setPreview(initialValue);
      setIsLocalFile(false);
    }
  }, [initialValue, selectedImage]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Revoke previous local file URL if it exists
    if (preview && isLocalFile) {
      URL.revokeObjectURL(preview);
    }

    setFile(selectedFile);

    // Create and set preview for local file
    const previewURL = URL.createObjectURL(selectedFile);
    setPreview(previewURL);
    setIsLocalFile(true);
    setSelectedImage(""); // Reset existing selection when new file is selected

    // Notify parent that we're working with a local file preview (but don't pass the local URL)
    // This prevents the parent from trying to use a temporary object URL
    onSelectImage("");
  };

  const uploadFile = async () => {
    if (!file) {
      toast.error("Please select a file first!");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to upload image: ${res.statusText}`);
      }

      const data = await res.json();
      const uploadedImageUrl = data.fileUrl;

      // Update state with the new image
      setExistingImages((prev) => [...prev, { url: uploadedImageUrl }]);
      setSelectedImage(uploadedImageUrl);
      setPreview(uploadedImageUrl);
      setIsLocalFile(false);
      setFile(null); // Reset file state after successful upload

      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Notify parent of the new uploaded image URL
      onSelectImage(uploadedImageUrl);

      toast.success("Image uploaded successfully!");
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(`Failed to upload image: ${error.message}`);
    }
  };

  const handleSelectExisting = (url) => {
    // If we had a local file preview, revoke it
    if (preview && isLocalFile) {
      URL.revokeObjectURL(preview);
    }

    setSelectedImage(url);
    setPreview(url);
    setIsLocalFile(false);
    setFile(null); // Reset file input

    // Reset the file input element
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    onSelectImage(url);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6">Device Image</Typography>

      {isLoading ? (
        <Typography>Loading images...</Typography>
      ) : (
        <>
          {/* Dropdown for selecting existing images */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Existing Image</InputLabel>
            <Select
              value={selectedImage || ""}
              onChange={(e) => handleSelectExisting(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select an image
              </MenuItem>
              {existingImages.length > 0 ? (
                existingImages.map((img, index) => (
                  <MenuItem key={index} value={img.url}>
                    {img.url}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No images available</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* File Input for New Image Upload */}
          <Box sx={{ mt: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              id="image-upload"
              style={{ display: "none" }}
            />
            <label htmlFor="image-upload">
              <Button variant="outlined" component="span" fullWidth>
                {file ? file.name : "Choose New Image"}
              </Button>
            </label>
          </Box>

          {/* Upload New Image Button */}
          <Button
            variant="contained"
            onClick={uploadFile}
            disabled={!file}
            fullWidth
            sx={{
              mt: 2,
              backgroundColor: isDarkMode ? "#BD93F9" : "#22C55E",
              "&:hover": {
                backgroundColor: isDarkMode ? "#A370E0" : "#16A34A",
              },
            }}
          >
            Upload New Image
          </Button>

          {/* Image Preview */}
          {preview && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">
                {isLocalFile
                  ? "Local Image Preview:"
                  : "Selected Image Preview:"}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  width: "100px",
                  height: "100px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 1,
                }}
              >
                <img
                  src={preview}
                  alt="Selected"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

DeviceImageUploader.propTypes = {
  onSelectImage: PropTypes.func.isRequired,
  initialValue: PropTypes.string,
};

export default DeviceImageUploader;
